import { createHydratedFileAttachment } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/internal/fileAttachmentUpdates.js';
import { useCallback, useRef, useState } from 'react';
export function useFileAttachmentState({ onDismissError: observeDismissError, }) {
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState([]);
    const removedIdsRef = useRef(new Set());
    const [attachmentMetadataById, setAttachmentMetadataById] = useState(new Map());
    const setError = useCallback((error) => {
        // A selection error replaces the banner without clearing an earlier upload failure.
        setErrors((current) => error === undefined ? [] : [...current.filter((entry) => entry.type !== error.type), error]);
    }, []);
    const removeAttachments = useCallback((ids) => {
        if (ids.length === 0)
            return;
        const idsToRemove = new Set(ids);
        ids.forEach((id) => removedIdsRef.current.add(id));
        setAttachmentMetadataById((current) => {
            if (!ids.some((id) => current.has(id)))
                return current;
            const next = new Map(current);
            ids.forEach((id) => next.delete(id));
            return next;
        });
        setFiles((current) => {
            const remaining = current.filter((file) => !idsToRemove.has(file.id));
            return remaining.length === current.length ? current : remaining;
        });
    }, []);
    const onRemoveFile = useCallback((id) => {
        removeAttachments([id]);
    }, [removeAttachments]);
    const addAttachments = useCallback((attachments) => {
        setAttachmentMetadataById((current) => {
            const withMetadata = attachments.filter((attachment) => attachment.metadata !== undefined);
            if (withMetadata.length === 0)
                return current;
            const next = new Map(current);
            for (const attachment of withMetadata) {
                next.set(attachment.url, attachment.metadata);
            }
            return next;
        });
        setFiles((current) => {
            const existingUrls = new Set(current.map((file) => file.url).filter(Boolean));
            const recoveredFiles = attachments.flatMap((attachment) => {
                if (existingUrls.has(attachment.url))
                    return [];
                existingUrls.add(attachment.url);
                return [
                    {
                        id: attachment.url,
                        file: new File([], attachment.filename),
                        fileName: attachment.filename,
                        fileSize: 0,
                        fileType: '',
                        previewTarget: {
                            fileName: attachment.filename,
                            kind: 'remote',
                            url: attachment.url,
                        },
                        status: 'success',
                        url: attachment.url,
                    },
                ];
            });
            return recoveredFiles.length > 0 ? [...current, ...recoveredFiles] : current;
        });
    }, []);
    const onDismissError = useCallback(() => {
        observeDismissError?.();
        setErrors([]);
    }, [observeDismissError]);
    const hydrateAttachments = useCallback((attachments) => {
        if (attachments.length === 0)
            return;
        setFiles((current) => [...current, ...attachments.map(createHydratedFileAttachment)]);
    }, []);
    const reset = useCallback(() => {
        setAttachmentMetadataById(new Map());
        setFiles((current) => {
            current.forEach((file) => removedIdsRef.current.add(file.id));
            return [];
        });
        setErrors([]);
    }, []);
    return {
        addAttachments,
        attachmentMetadataById,
        errorMessage: errors.at(-1)?.message,
        files,
        hasUploadFailure: errors.some((error) => error.type === 'upload'),
        hydrateAttachments,
        onDismissError,
        onRemoveFile,
        removeAttachments,
        removedIdsRef,
        reset,
        setError,
        setFiles,
        setAttachmentMetadataById,
    };
}
//# sourceMappingURL=useFileAttachmentState.js.map